import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import type { SignalTier } from "./SignalFeedSlice";
import { get } from "../../Services/ApiRequest";
import { API_URLS } from "../../Utils/Constants/APIConstants";

/** §8.2 — one row per account this persona is assigned to, not a flat
 * event feed (signal-service-design-note.md). */
export interface PersonaHistoryAccountSummary {
  account_id: string;
  account_name: string;
  persona: string | null;
  is_primary: boolean;
  active_count: number;
  resolved_count: number;
  last_activity_at: string | null;
}

export interface SignalHistoryInteraction {
  event_type:
    | "delivered"
    | "viewed"
    | "cta_clicked"
    | "dismissed"
    | "snoozed"
    | "resolved_after_action";
  cta_label: string | null;
  channel: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
}

/** §8.1 — every signal this account has ever had, all states. */
export interface AccountSignalHistoryEvent {
  signal_firing_id: string;
  signal_key: string;
  tier: SignalTier;
  state: "active" | "acknowledged" | "snoozed" | "resolved";
  first_fired_at: string;
  last_evaluated_at: string;
  resolved_at: string | null;
  resolution_reason: string | null;
  evidence: {
    triggeredFields?: Record<string, unknown>;
    conditionString?: string;
    ruleVersion?: string;
  } | null;
  interactions: SignalHistoryInteraction[];
}

interface SignalHistoryState {
  summary: PersonaHistoryAccountSummary[];
  summaryLoading: boolean;
  summaryError: string | null;
  accountId: string | null;
  events: AccountSignalHistoryEvent[];
  eventsLoading: boolean;
  eventsError: string | null;
  /** False when the org's Open Actions is Hidden (e.g. signals not turned
   * on for it): the server answers with no events, and the page says why. */
  historyAvailable: boolean;
}

const initialState: SignalHistoryState = {
  summary: [],
  summaryLoading: false,
  summaryError: null,
  accountId: null,
  events: [],
  eventsLoading: false,
  eventsError: null,
  historyAvailable: true,
};

export const fetchSignalHistorySummary = createAsyncThunk(
  "signalHistory/fetchSummary",
  async (_: void, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getSignalHistorySummary());
      const content = (response as any)?.content || response;
      return (content?.accounts || []) as PersonaHistoryAccountSummary[];
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch signal history"
      );
    }
  }
);

export const fetchAccountSignalHistory = createAsyncThunk(
  "signalHistory/fetchAccountEvents",
  async (accountId: string, { rejectWithValue }) => {
    try {
      const response = await get(API_URLS.getAccountSignalHistory(accountId));
      const content = (response as any)?.content || response;
      return {
        accountId,
        events: (content?.events || []) as AccountSignalHistoryEvent[],
        historyAvailable: content?.presentation?.history_available !== false,
      };
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch account signal history"
      );
    }
  }
);

/** My Accounts → Actions → History: the lifecycle records for this user's
 * assigned accounts. Rejects with the error's message so the tab can show
 * it as-is. */
export const fetchMyActionHistory = createAsyncThunk(
  "signalHistory/fetchMyActionHistory",
  async (_: void, { rejectWithValue }) => {
    try {
      return await get(API_URLS.getSignalActionHistory());
    } catch (e: unknown) {
      return rejectWithValue(
        e instanceof Error ? e.message : "Couldn't load action history."
      );
    }
  }
);

const signalHistorySlice = createSlice({
  name: "signalHistory",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchSignalHistorySummary.pending, state => {
        state.summaryLoading = true;
        state.summaryError = null;
      })
      .addCase(fetchSignalHistorySummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload;
      })
      .addCase(fetchSignalHistorySummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.summaryError =
          (action.payload as string) || action.error.message || "Unknown error";
      })
      .addCase(fetchAccountSignalHistory.pending, state => {
        state.eventsLoading = true;
        state.eventsError = null;
      })
      .addCase(fetchAccountSignalHistory.fulfilled, (state, action) => {
        state.eventsLoading = false;
        state.accountId = action.payload.accountId;
        state.events = action.payload.events;
        state.historyAvailable = action.payload.historyAvailable;
      })
      .addCase(fetchAccountSignalHistory.rejected, (state, action) => {
        state.eventsLoading = false;
        state.eventsError =
          (action.payload as string) || action.error.message || "Unknown error";
      });
  },
});

export default signalHistorySlice.reducer;
